import React, { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import './Vacations.css';

const Vacations = () => {
  const [moderators, setModerators] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [vacationRequests, setVacationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [rejectModal, setRejectModal] = useState({
    isOpen: false,
    requestId: null,
    onConfirm: null
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRequests, setShowRequests] = useState(false);

  useEffect(() => {
    fetchModerators();
    fetchVacationRequests();
  }, []);

  useEffect(() => {
    fetchVacations();
  }, [currentMonth]);

  const fetchModerators = async () => {
    try {
      const token = localStorage.getItem('token');
      // Параметр hide_curator_roles=1 — применить настройку «Скрывать модераторов с ролями...» из раздела Отображение
      const response = await fetch('/api/managers?hide_curator_roles=1', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Фильтруем только активных модераторов
        const activeModerators = data.filter(m => !m.deleted_at);
        // Сортируем по серверам (сначала по номеру сервера, затем по никнейму)
        const sortedModerators = activeModerators.sort((a, b) => {
          // Сначала сравниваем по серверу
          if (a.server !== b.server) {
            // Извлекаем номер сервера из строки (например, "RU1_MRP" -> 1)
            const getServerNumber = (server) => {
              if (!server) return 9999; // Серверы без номера в конец
              const match = server.match(/RU(\d+)/i);
              return match ? parseInt(match[1], 10) : 9999;
            };
            const serverNumA = getServerNumber(a.server);
            const serverNumB = getServerNumber(b.server);
            if (serverNumA !== serverNumB) {
              return serverNumA - serverNumB;
            }
            // Если номера одинаковые, сортируем по названию сервера
            return (a.server || '').localeCompare(b.server || '');
          }
          // Если серверы одинаковые, сортируем по никнейму
          return (a.nickname || '').localeCompare(b.nickname || '');
        });
        setModerators(sortedModerators);
      }
    } catch (err) {
      console.error('Error fetching moderators:', err);
    }
  };

  const fetchVacations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Получаем первый и последний день месяца
      // Создаем даты в локальном времени в полдень для избежания проблем с часовыми поясами
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const startDate = new Date(year, month, 1, 12, 0, 0, 0);
      const endDate = new Date(year, month + 1, 0, 12, 0, 0, 0);
      
      const startDateStr = formatDateForServer(startDate);
      const endDateStr = formatDateForServer(endDate);

      const response = await fetch(
        `/api/vacations?startDate=${startDateStr}&endDate=${endDateStr}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setVacations(data);
      } else {
        setError('Ошибка при загрузке отпусков');
      }
    } catch (err) {
      console.error('Error fetching vacations:', err);
      setError('Ошибка при загрузке отпусков');
    } finally {
      setLoading(false);
    }
  };

  const fetchVacationRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/vacations/requests', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setVacationRequests(data);
      }
    } catch (err) {
      console.error('Error fetching vacation requests:', err);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vacations/requests/${requestId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchVacationRequests();
        await fetchVacations();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Ошибка при одобрении запроса');
      }
    } catch (err) {
      console.error('Error approving request:', err);
      setError('Ошибка при одобрении запроса');
    }
  };

  const handleRejectRequest = async (requestId, reason) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vacations/requests/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rejectionReason: reason })
      });

      if (response.ok) {
        await fetchVacationRequests();
        setRejectModal({ isOpen: false, requestId: null, onConfirm: null });
        setRejectionReason('');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Ошибка при отклонении запроса');
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
      setError('Ошибка при отклонении запроса');
    }
  };

  const formatDateForDisplay = (dateInput) => {
    // Принимает как строку (YYYY-MM-DD), так и Date объект
    let date;
    if (typeof dateInput === 'string') {
      date = new Date(dateInput + 'T12:00:00');
    } else {
      date = dateInput;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatDateForServer = (date) => {
    // Используем локальные методы напрямую, без создания нового Date объекта
    // Это гарантирует, что мы получаем именно ту дату, которую видит пользователь
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };


  const handleVacationClick = (moderatorDiscordId, date) => {
    const moderator = moderators.find(m => m.discord_id === moderatorDiscordId);
    const dateStr = formatDateForServer(date);
    
    // Используем ту же логику, что и в isVacation для проверки
    const existingVacation = vacations.find(v => {
      if (!v.vacation_date) return false;
      // Извлекаем только дату (YYYY-MM-DD) из строки (на случай если придет с временем)
      const vacationDate = String(v.vacation_date).split('T')[0].split(' ')[0].trim();
      return v.moderator_discord_id === moderatorDiscordId && vacationDate === dateStr;
    });

    if (existingVacation) {
      // Удаление отпуска
      setConfirmModal({
        isOpen: true,
        title: 'Удаление отпуска',
        message: `Вы уверены, что хотите удалить отпуск для модератора ${moderator?.nickname || 'неизвестно'} на дату ${formatDateForDisplay(date)}?`,
        onConfirm: () => {
          toggleVacation(moderatorDiscordId, date, true);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      });
    } else {
      // Добавление отпуска
      setConfirmModal({
        isOpen: true,
        title: 'Выдача отпуска',
        message: `Вы уверены, что хотите выдать отпуск модератору ${moderator?.nickname || 'неизвестно'} на дату ${formatDateForDisplay(date)}?`,
        onConfirm: () => {
          toggleVacation(moderatorDiscordId, date, false);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      });
    }
  };

  const toggleVacation = async (moderatorDiscordId, date, isDelete) => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      
      // Убеждаемся, что мы работаем с правильной датой
      // Создаем дату в локальном времени в полдень для избежания проблем с часовыми поясами
      const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
      const dateStr = formatDateForServer(localDate);

      if (isDelete) {
        // Удаляем отпуск
        const response = await fetch(
          `/api/vacations?moderatorDiscordId=${moderatorDiscordId}&vacationDate=${dateStr}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          await fetchVacations();
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.error || 'Ошибка при удалении отпуска');
        }
      } else {
        // Добавляем отпуск
        const response = await fetch('/api/vacations', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            moderatorDiscordId,
            vacationDate: dateStr
          })
        });

        if (response.ok) {
          await fetchVacations();
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.error || 'Ошибка при добавлении отпуска');
        }
      }
    } catch (err) {
      console.error('Error toggling vacation:', err);
      setError('Ошибка при изменении отпуска');
    }
  };

  const isVacation = (moderatorDiscordId, date) => {
    const dateStr = formatDateForServer(date);
    // Дата из базы теперь всегда приходит как строка YYYY-MM-DD благодаря TO_CHAR
    const found = vacations.some(v => {
      if (!v.vacation_date) return false;
      // Извлекаем только дату (YYYY-MM-DD) из строки (на случай если придет с временем)
      const vacationDate = String(v.vacation_date).split('T')[0].split(' ')[0].trim();
      return v.moderator_discord_id === moderatorDiscordId && vacationDate === dateStr;
    });
    return found;
  };

  // Подсчет количества отпусков модератора за месяц
  const getModeratorVacationsCount = (moderatorDiscordId) => {
    return vacations.filter(v => {
      if (!v.vacation_date || v.moderator_discord_id !== moderatorDiscordId) return false;
      const vacationDate = String(v.vacation_date).split('T')[0].split(' ')[0].trim();
      // Проверяем, что отпуск в текущем месяце
      const vacationDateObj = new Date(vacationDate + 'T12:00:00');
      return vacationDateObj.getMonth() === currentMonth.getMonth() && 
             vacationDateObj.getFullYear() === currentMonth.getFullYear();
    }).length;
  };

  // Подсчет количества отпусков в конкретный день
  const getDayVacationsCount = (date) => {
    const dateStr = formatDateForServer(date);
    return vacations.filter(v => {
      if (!v.vacation_date) return false;
      const vacationDate = String(v.vacation_date).split('T')[0].split(' ')[0].trim();
      return vacationDate === dateStr;
    }).length;
  };

  const getDayOfWeek = (date) => {
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days[date.getDay()];
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Добавляем только дни текущего месяца
    // Создаем даты в локальном времени в полдень (12:00:00) для избежания проблем с часовыми поясами
    // Использование полдня гарантирует, что при любом часовом поясе дата не сдвинется
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day, 12, 0, 0, 0);
      days.push(date);
    }
    
    return days;
  };

  const changeMonth = (direction) => {
    setCurrentMonth(prev => {
      const year = prev.getFullYear();
      const month = prev.getMonth();
      const newMonth = month + direction;
      
      // Правильно обрабатываем переход через границы года
      if (newMonth < 0) {
        return new Date(year - 1, 11, 1);
      } else if (newMonth > 11) {
        return new Date(year + 1, 0, 1);
      } else {
        return new Date(year, newMonth, 1);
      }
    });
  };

  const goToCurrentMonth = () => {
    setCurrentMonth(new Date());
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentMonth.getMonth() && 
           date.getFullYear() === currentMonth.getFullYear();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const days = getDaysInMonth();

  return (
    <div className="vacations-container">
      <div className="vacations-header">
        <div className="vacations-header-top">
          <div className="vacations-month-navigation">
            <button
              type="button"
              className="vacations-month-nav vacations-month-nav-prev"
              onClick={() => changeMonth(-1)}
              title="Предыдущий месяц"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <span className="vacations-month-title">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>
            <button
              type="button"
              className="vacations-month-nav vacations-month-nav-next"
              onClick={() => changeMonth(1)}
              title="Следующий месяц"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            <button 
              className="vacations-current-month-btn"
              onClick={goToCurrentMonth}
            >
              Текущий месяц
            </button>
            <button 
              className={`vacations-toggle-requests-btn ${showRequests ? 'active' : ''}`}
              onClick={() => setShowRequests(!showRequests)}
            >
              {showRequests ? 'Таблица' : 'Запросы отпуска'}
              {vacationRequests.length > 0 && (
                <span className="vacations-requests-count-badge">{vacationRequests.length}</span>
              )}
            </button>
          </div>
        </div>
        {error && <div className="vacations-error">{error}</div>}
      </div>

      {showRequests ? (
        <div className="vacations-requests-container">
          {vacationRequests.length > 0 ? (
            <>
              <div className="section-header-main">
                <h2 className="section-title-main">ЗАПРОСЫ НА ОТПУСК</h2>
              </div>
              <div className="vacations-requests-grid">
                {vacationRequests.map(request => (
                  <div key={request.id} className="vacations-request-card">
                    <div className="vacations-request-card-header">
                      <div className="vacations-request-card-moderator">
                        <div className="vacations-request-card-avatar">
                          {request.moderator_nickname?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div className="vacations-request-card-moderator-info">
                          <div className="vacations-request-card-name">{request.moderator_nickname}</div>
                          {request.moderator_server && (
                            <div className="vacations-request-card-server">{request.moderator_server}</div>
                          )}
                        </div>
                      </div>
                      <div className="vacations-request-card-date">
                        {new Date(request.created_at).toLocaleDateString('ru-RU', { 
                          day: '2-digit', 
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    
                    <div className="vacations-request-card-section">
                      <div className="vacations-request-card-label">Дни отпуска</div>
                      <div className="vacations-request-card-dates">
                        {request.vacation_dates.map((d, idx) => (
                          <span key={idx} className="vacations-request-card-date-badge">
                            {formatDateForDisplay(d)}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="vacations-request-card-section">
                      <div className="vacations-request-card-label">Причина</div>
                      <div className="vacations-request-card-reason">
                        {request.reason}
                      </div>
                    </div>

                    <div className="vacations-request-card-actions">
                      <button
                        className="vacations-request-card-approve"
                        onClick={() => handleApproveRequest(request.id)}
                      >
                        Одобрить
                      </button>
                      <button
                        className="vacations-request-card-reject"
                        onClick={() => setRejectModal({ isOpen: true, requestId: request.id, onConfirm: () => handleRejectRequest(request.id, rejectionReason) })}
                      >
                        Отклонить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="vacations-requests-empty">
              <div className="vacations-requests-empty-text">Нет запросов на отпуск</div>
            </div>
          )}
        </div>
      ) : (
        <div className="vacations-content">
          {loading ? (
            <div className="vacations-loading">Загрузка...</div>
          ) : (
            <div className="vacations-table-container">
            <div className="vacations-table-wrapper">
              <table className="vacations-table">
                <thead>
                  <tr>
                    <th className="vacations-table-moderator-col">Модератор</th>
                    <th className="vacations-table-total-col">Итог</th>
                    {days.map((date, index) => {
                      const isTodayDate = isToday(date);
                      const isSunday = date.getDay() === 0;
                      return (
                        <th
                          key={index}
                          className={`vacations-table-date-header ${isTodayDate ? 'vacations-day-today' : ''} ${isSunday ? 'vacations-week-end' : ''}`}
                        >
                          <div className="vacations-date-header-content">
                            <div className="vacations-day-of-week">{getDayOfWeek(date)}</div>
                            <div className="vacations-day-number">{date.getDate()}</div>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {moderators.map(moderator => {
                    const vacationsCount = getModeratorVacationsCount(moderator.discord_id);
                    return (
                      <tr key={moderator.id}>
                        <td className="vacations-table-moderator-cell">
                          <div className="vacations-moderator-cell-content">
                            <span className="vacations-moderator-cell-nickname">{moderator.nickname}</span>
                            <span className="vacations-moderator-cell-server">{moderator.server}</span>
                          </div>
                        </td>
                        <td className="vacations-table-total-cell">
                          <div className="vacations-total-value">{vacationsCount}</div>
                        </td>
                        {days.map((date, index) => {
                          const hasVacation = isVacation(moderator.discord_id, date);
                          const isTodayDate = isToday(date);
                          const isSunday = date.getDay() === 0;
                          const cellClasses = [
                            'vacations-table-cell',
                            hasVacation ? 'vacations-cell-has-vacation' : '',
                            isTodayDate ? 'vacations-day-today' : '',
                            isSunday ? 'vacations-week-end' : ''
                          ].filter(Boolean).join(' ');
                          return (
                            <td
                              key={index}
                              className={cellClasses}
                              onClick={() => handleVacationClick(moderator.discord_id, date)}
                              title={hasVacation ? 'Удалить отпуск' : 'Добавить отпуск'}
                              style={hasVacation ? { backgroundColor: 'rgba(239, 68, 68, 0.3)', borderColor: 'rgba(239, 68, 68, 0.5)' } : {}}
                            >
                            {hasVacation && (
                              <div className="vacations-indicator">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 12h14M12 5l7 7-7 7"></path>
                                </svg>
                              </div>
                            )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {/* Итоговая строка с количеством отпусков по дням */}
                  <tr className="vacations-table-total-row">
                    <td className="vacations-table-moderator-cell vacations-table-total-label-cell">
                      <div className="vacations-total-label">Итого</div>
                    </td>
                    <td className="vacations-table-total-cell vacations-table-total-sum-cell">
                      <div className="vacations-total-value">{vacations.length}</div>
                    </td>
                    {days.map((date, index) => {
                      const dayCount = getDayVacationsCount(date);
                      const isSunday = date.getDay() === 0;
                      return (
                        <td
                          key={index}
                          className={`vacations-table-total-cell ${isSunday ? 'vacations-week-end' : ''}`}
                        >
                          <div className="vacations-total-day-value">{dayCount > 0 ? dayCount : ''}</div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      {rejectModal.isOpen && (
        <div className="vacations-reject-modal-overlay" onClick={() => setRejectModal({ isOpen: false, requestId: null, onConfirm: null })}>
          <div className="vacations-reject-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Причина отклонения</h3>
            <textarea
              className="vacations-reject-reason-input"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Укажите причину отклонения запроса..."
              rows={4}
            />
            <div className="vacations-reject-modal-actions">
              <button
                className="vacations-reject-cancel"
                onClick={() => {
                  setRejectModal({ isOpen: false, requestId: null, onConfirm: null });
                  setRejectionReason('');
                }}
              >
                Отмена
              </button>
              <button
                className="vacations-reject-confirm"
                onClick={() => {
                  if (rejectionReason.trim()) {
                    handleRejectRequest(rejectModal.requestId, rejectionReason);
                  }
                }}
                disabled={!rejectionReason.trim()}
              >
                Отклонить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vacations;

